'use client';

import React, { useState } from 'react';
import { ExternalLink, Clock, CheckCircle, RefreshCw } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import { toast } from 'sonner';
import type { Invoice } from './InvoiceContent';

const invoiceLineItems: Record<string, { id: string; description: string; qty: number; unitPrice: string; total: string }[]> = {
  'inv-10482': [
    { id: 'li-inv10482-1', description: 'Lanyards — dye-sub print, 1000 units', qty: 1000, unitPrice: '£6.80', total: '£6,800' },
    { id: 'li-inv10482-2', description: 'Artwork setup and proofing', qty: 1, unitPrice: '£800.00', total: '£800' },
    { id: 'li-inv10482-3', description: 'Express delivery — next day', qty: 1, unitPrice: '£800.00', total: '£800' },
  ],
  'inv-10478': [
    { id: 'li-inv10478-1', description: 'Gildan 64000 polo — embroidered NHS logo — assorted sizes', qty: 450, unitPrice: '£22.40', total: '£10,080' },
    { id: 'li-inv10478-2', description: 'Embroidery setup — 5 trust variants', qty: 5, unitPrice: '£144.00', total: '£720' },
    { id: 'li-inv10478-3', description: 'Packing and labelling per NHS spec', qty: 1, unitPrice: '£840.00', total: '£840' },
  ],
  'inv-10481': [
    { id: 'li-inv10481-1', description: 'Gildan 18500 hoodie navy — embroidered logo — assorted sizes', qty: 250, unitPrice: '£52.80', total: '£13,200' },
    { id: 'li-inv10481-2', description: 'Embroidery setup charge', qty: 1, unitPrice: '£1,020.00', total: '£1,020' },
  ],
};

const defaultLineItems = [
  { id: 'li-default-1', description: 'Branded merchandise — see linked document for detail', qty: 1, unitPrice: '—', total: '—' },
];

const paymentHistory: Record<string, { id: string; date: string; amount: string; method: string; status: string; ref: string }[]> = {
  'inv-10480': [
    { id: 'pay-480-1', date: '18/08/2026', amount: '£3,150', method: 'BACS transfer', status: 'received', ref: 'PAY-4821' },
  ],
  'inv-10475': [
    { id: 'pay-475-1', date: '20/08/2026', amount: '£14,200', method: 'BACS transfer', status: 'made', ref: 'PAY-4819' },
  ],
  'inv-10474': [
    { id: 'pay-474-1', date: '10/08/2026', amount: '£11,760', method: 'BACS transfer', status: 'received', ref: 'PAY-4810' },
  ],
  'inv-10472': [
    { id: 'pay-472-1', date: '04/08/2026', amount: '£8,640', method: 'BACS transfer', status: 'received', ref: 'PAY-4802' },
  ],
  'inv-10471': [
    { id: 'pay-471-1', date: '16/08/2026', amount: '£7,800', method: 'BACS transfer', status: 'made', ref: 'PAY-4816' },
  ],
};

const invoiceStatuses = ['draft', 'sent', 'due', 'overdue', 'paid', 'reconciled'];

export default function InvoiceDetailPanel({ invoice }: { invoice: Invoice }) {
  const lineItems = invoiceLineItems[invoice.id] ?? defaultLineItems;
  const payments = paymentHistory[invoice.id] ?? [];
  const [currentStatus, setCurrentStatus] = useState(invoice.status);
  const [notes, setNotes] = useState('');

  const handleStatusChange = (status: string) => {
    setCurrentStatus(status);
    // Backend integration point: PATCH /api/invoices/{invoice.id}/status
    toast.success(`Invoice status updated to ${status}`);
  };

  const handleReconcile = () => {
    // Backend integration point: POST /api/reconciliation { invoiceId: invoice.id }
    toast.success('Reconciliation record created');
    setCurrentStatus('reconciled');
  };

  return (
    <div className="space-y-6">
      {/* Invoice header */}
      <div
        className="rounded-lg p-4 border"
        style={{
          backgroundColor: invoice.isOverdue ? 'var(--amber-light)' : 'var(--cloud)',
          borderColor: invoice.isOverdue ? '#FDE8C4' : 'var(--border)',
        }}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <p className="text-xs font-mono font-semibold" style={{ color: 'var(--slate)' }}>{invoice.invoiceNumber}</p>
              <span
                className="text-xs font-semibold px-1.5 py-0.5 rounded"
                style={{
                  backgroundColor: invoice.type === 'AR' ? 'var(--teal-light)' : 'var(--slate-light)',
                  color: invoice.type === 'AR' ? 'var(--accent)' : 'var(--slate)',
                }}
              >
                {invoice.type}
              </span>
            </div>
            <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>{invoice.jobName}</p>
          </div>
          <StatusBadge status={currentStatus as never} pulse={invoice.isOverdue} />
        </div>

        {invoice.isOverdue && (
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-md mb-3"
            style={{ backgroundColor: '#FEE2E2', border: '1px solid #FECACA' }}
          >
            <Clock size={13} style={{ color: '#DC2626' }} />
            <p className="text-xs font-semibold" style={{ color: '#DC2626' }}>{invoice.daysLabel} — payment required immediately</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          {[
            { label: invoice.type === 'AR' ? 'Client' : 'Supplier', value: invoice.counterparty },
            { label: 'Job ID', value: invoice.jobId },
            { label: 'Issue date', value: invoice.issueDate },
            { label: 'Due date', value: invoice.dueDate },
            { label: 'Invoice total', value: invoice.amount },
            { label: invoice.type === 'AR' ? 'Linked SO' : 'Linked PO', value: invoice.linkedDoc },
          ].map(f => (
            <div key={`inv-field-${f.label}`}>
              <p className="text-xs font-medium" style={{ color: 'var(--slate)' }}>{f.label}</p>
              <p
                className="text-sm font-semibold mt-0.5"
                style={{
                  color: f.label.includes('Linked') && f.value !== '—' ? 'var(--accent)' :
                    f.label === 'Invoice total' ? 'var(--foreground)' : 'var(--foreground)',
                }}
              >
                {f.value}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Status management */}
      <div>
        <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--foreground)' }}>Update status</h4>
        <div className="flex flex-wrap gap-2">
          {invoiceStatuses.map(s => (
            <button
              key={`inv-status-btn-${s}`}
              onClick={() => handleStatusChange(s)}
              className="px-3 py-1.5 text-xs font-medium rounded-md border transition-all"
              style={{
                backgroundColor: currentStatus === s ? 'var(--ink)' : 'var(--card)',
                color: currentStatus === s ? 'white' : 'var(--slate)',
                borderColor: currentStatus === s ? 'var(--ink)' : 'var(--border)',
              }}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        {(currentStatus === 'paid') && (
          <button
            onClick={handleReconcile}
            className="mt-3 flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md text-white transition-all"
            style={{ backgroundColor: 'var(--accent)' }}
          >
            <RefreshCw size={14} />
            Reconcile this invoice
          </button>
        )}
      </div>

      {/* Line items */}
      <div>
        <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--foreground)' }}>Line items</h4>
        <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <table className="w-full text-xs">
            <thead>
              <tr style={{ backgroundColor: 'var(--cloud)', borderBottom: '1px solid var(--border)' }}>
                {['Description', 'Qty', 'Unit price', 'Total'].map(h => (
                  <th key={`inv-li-th-${h}`} className="text-left px-3 py-2 font-semibold uppercase tracking-wider" style={{ color: 'var(--slate)', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lineItems.map(li => (
                <tr key={li.id} className="border-b last:border-0" style={{ borderColor: 'var(--border)' }}>
                  <td className="px-3 py-2.5" style={{ color: 'var(--foreground)' }}>{li.description}</td>
                  <td className="px-3 py-2.5 tabular-nums" style={{ color: 'var(--slate)' }}>{li.qty}</td>
                  <td className="px-3 py-2.5 tabular-nums" style={{ color: 'var(--slate)' }}>{li.unitPrice}</td>
                  <td className="px-3 py-2.5 tabular-nums font-semibold" style={{ color: 'var(--foreground)' }}>{li.total}</td>
                </tr>
              ))}
              <tr style={{ backgroundColor: 'var(--cloud)', borderTop: '1px solid var(--border)' }}>
                <td colSpan={3} className="px-3 py-2.5 font-semibold text-right" style={{ color: 'var(--foreground)' }}>Invoice total</td>
                <td className="px-3 py-2.5 tabular-nums font-bold" style={{ color: 'var(--foreground)' }}>{invoice.amount}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment history */}
      <div>
        <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--foreground)' }}>Payment history</h4>
        {payments.length === 0 ? (
          <div
            className="rounded-lg border px-4 py-6 text-center"
            style={{ borderColor: 'var(--border)', backgroundColor: 'var(--cloud)' }}
          >
            <Clock size={20} className="mx-auto mb-2" style={{ color: 'var(--border)' }} />
            <p className="text-xs" style={{ color: 'var(--slate)' }}>No payments recorded against this invoice yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {payments.map(pay => (
              <div
                key={pay.id}
                className="flex items-center justify-between rounded-lg border px-4 py-3"
                style={{ backgroundColor: 'var(--teal-light)', borderColor: 'var(--accent)' }}
              >
                <div className="flex items-center gap-3">
                  <CheckCircle size={16} style={{ color: 'var(--accent)' }} />
                  <div>
                    <p className="text-xs font-semibold" style={{ color: 'var(--accent)' }}>
                      {pay.status === 'received' ? 'Payment received' : 'Payment made'} — {pay.date}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--slate)' }}>{pay.method} · Ref: {pay.ref}</p>
                  </div>
                </div>
                <span className="tabular-nums font-bold text-sm" style={{ color: 'var(--accent)' }}>{pay.amount}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Linked doc */}
      <div
        className="flex items-center gap-2 px-4 py-3 rounded-lg border"
        style={{ backgroundColor: 'var(--cloud)', borderColor: 'var(--border)' }}
      >
        <ExternalLink size={13} style={{ color: 'var(--accent)' }} />
        <p className="text-xs" style={{ color: 'var(--slate)' }}>
          {invoice.type === 'AR' ? 'Sales order' : 'Purchase order'}:{' '}
          <span className="font-semibold" style={{ color: 'var(--accent)' }}>{invoice.linkedDoc}</span>
        </p>
      </div>

      {/* Notes */}
      <div>
        <h4 className="text-sm font-semibold mb-2" style={{ color: 'var(--foreground)' }}>Notes</h4>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Add notes about payment terms, disputes, or client communications…"
          rows={3}
          className="w-full px-3 py-2.5 text-sm rounded-md border outline-none resize-none"
          style={{ borderColor: 'var(--border)', backgroundColor: 'var(--cloud)', color: 'var(--foreground)' }}
        />
        <button
          onClick={() => { if (notes.trim()) toast.success('Notes saved'); }}
          disabled={!notes.trim()}
          className="mt-2 px-4 py-2 text-xs font-medium rounded-md text-white transition-all disabled:opacity-50"
          style={{ backgroundColor: 'var(--accent)' }}
        >
          Save notes
        </button>
      </div>
    </div>
  );
}